import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { IssueCommentDto, IssueTimelineEventDto } from '@aiai/shared';
import {
  CheckCircle2,
  CircleDot,
  GitMerge,
  GitPullRequest,
  Hourglass,
  Inbox,
  MessageSquare,
  Pencil,
  Play,
  XCircle,
} from 'lucide-react';
import { fetchIssueComments, fetchIssueTimeline } from '../api';
import { qk } from '../api/queryKeys';
import { useAuth } from '../auth/useAuth';
import { useCapabilities } from '../hooks/useCapabilities';
import { formatRelative } from '../lib/format';
import { Markdown } from './Markdown';
import { CommentCard, CommentComposer } from './IssueComments';
import { ErrorState, LoadingState } from './States';
import { errorMessage } from '../lib/api';
import { useWorkspacePath } from '../hooks/useWorkspacePath';

function eventIcon(type: string, title: string) {
  const cls = 'h-3.5 w-3.5';
  switch (type) {
    case 'created':
      return <CircleDot className={`${cls} text-signal`} />;
    case 'filed':
      return <Inbox className={`${cls} text-signal`} />;
    case 'run':
      return <Play className={`${cls} text-ink-muted`} />;
    case 'pr':
      return <GitPullRequest className={`${cls} text-info`} />;
    case 'auto_merge':
      return title.startsWith('Auto-merged') ? (
        <GitMerge className={`${cls} text-success`} />
      ) : (
        <Hourglass className={`${cls} text-warning`} />
      );
    case 'merged':
      return <GitMerge className={`${cls} text-success`} />;
    case 'action':
    case 'action_out':
      return <Pencil className={`${cls} text-signal`} />;
    case 'filed_out':
      return <Inbox className={`${cls} text-ink-muted`} />;
    case 'closed_manually':
      return <XCircle className={`${cls} text-ink-muted`} />;
    case 'feedback_triaged':
      return <MessageSquare className={`${cls} text-signal`} />;
    case 'feedback_resolved':
      return <CheckCircle2 className={`${cls} text-success`} />;
    case 'auto_approved':
      return <CheckCircle2 className={`${cls} text-signal`} />;
    case 'auto_held':
      return <Hourglass className={`${cls} text-warning`} />;
    case 'note':
      return <MessageSquare className={`${cls} text-ink-muted`} />;
    default:
      return <CircleDot className={`${cls} text-ink-faint`} />;
  }
}

/** "user:a@b.c" → a@b.c, "run:<uuid>" → linked run chip, "loop:X" → X. */
function Actor({ actor }: { actor: string | null }) {
  const { wp } = useWorkspacePath();
  if (!actor || actor === 'system') return null;
  const [kind, ...rest] = actor.split(':');
  const value = rest.join(':');
  if (kind === 'run' && value) {
    return (
      <Link
        to={wp(`/runs/${value}`)}
        className="font-mono text-2xs text-signal hover:underline"
      >
        run {value.slice(0, 8)}
      </Link>
    );
  }
  return (
    <span className="text-2xs text-ink-muted">
      {kind === 'user' || kind === 'loop' ? value : actor}
    </span>
  );
}

function TimelineEvent({ event }: { event: IssueTimelineEventDto }) {
  const { wp } = useWorkspacePath();
  // Run entries link into the run detail — the whole title is the click
  // target, which is why the run history list could be folded into here.
  const runLink =
    event.type === 'run' && event.runId ? wp(`/runs/${event.runId}`) : null;
  return (
    <li className="relative pl-8">
      <span className="absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full border border-line bg-surface-2">
        {eventIcon(event.type, event.title)}
      </span>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-sm text-ink">
          {runLink ? (
            <Link to={runLink} className="hover:underline">
              {event.title}
            </Link>
          ) : event.url ? (
            <a
              href={event.url}
              target="_blank"
              rel="noreferrer noopener"
              className="hover:underline"
            >
              {event.title}
            </a>
          ) : (
            event.title
          )}
        </span>
        {event.relatedIssue && (
          <Link
            to={wp(`/issues/${event.relatedIssue.code ?? event.relatedIssue.id}`)}
            className="max-w-xs truncate text-xs text-signal hover:underline"
            title={
              event.relatedIssue.code
                ? `${event.relatedIssue.code} · ${event.relatedIssue.title}`
                : event.relatedIssue.title
            }
          >
            {event.relatedIssue.code && (
              <span className="font-mono">{event.relatedIssue.code} </span>
            )}
            {event.relatedIssue.title}
          </Link>
        )}
        {!runLink && <Actor actor={event.actor} />}
        {event.runId && event.actor !== `run:${event.runId}` && (
          <Link
            to={wp(`/runs/${event.runId}`)}
            className="font-mono text-2xs text-ink-faint hover:underline"
          >
            run {event.runId.slice(0, 8)}
          </Link>
        )}
        <span className="text-2xs text-ink-faint">
          {formatRelative(event.at)}
        </span>
      </div>
      {event.detail &&
        (event.type === 'pr' || event.type === 'note' ? (
          // PR descriptions and agent notes are markdown — render them like
          // comments so they display with proper formatting.
          <div className="mt-1 rounded-md border border-line bg-surface-2 px-3 py-2">
            <Markdown className="text-xs">{event.detail}</Markdown>
          </div>
        ) : (
          <p className="mt-1 rounded-md border border-line bg-surface-2 px-3 py-2 text-xs leading-relaxed text-ink-muted">
            {event.detail}
          </p>
        ))}
    </li>
  );
}

/** A user comment rendered as a timeline entry, matching the medallion rail. */
function TimelineComment({
  comment,
  issueId,
  currentUserId,
  canModerate,
  readOnly,
}: {
  comment: IssueCommentDto;
  issueId: string;
  currentUserId: string;
  canModerate: boolean;
  readOnly: boolean;
}) {
  return (
    <li className="relative pl-8">
      <span className="absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full border border-line bg-surface-2">
        <MessageSquare className="h-3.5 w-3.5 text-ink-muted" />
      </span>
      <CommentCard
        comment={comment}
        issueId={issueId}
        currentUserId={currentUserId}
        canModerate={canModerate}
        readOnly={readOnly}
      />
    </li>
  );
}

type FeedItem =
  | { at: string; kind: 'event'; event: IssueTimelineEventDto }
  | { at: string; kind: 'comment'; comment: IssueCommentDto };

/**
 * GitHub-style governance feed at the bottom of an issue: creation and
 * provenance, every run with its outcome, PRs and (auto-)merges, worker
 * actions with their notes, and user notes interleaved chronologically — with
 * a compose box at the foot so notes are added straight into the timeline.
 */
export function IssueTimeline({
  issueId,
  readOnly = false,
  heldForYou = false,
  onApprove,
  approving = false,
  runnable = false,
  runAgain = false,
  onRun,
  running = false,
}: {
  issueId: string;
  readOnly?: boolean;
  /** The composer at the foot is the single action bar — see CommentComposer. */
  heldForYou?: boolean;
  onApprove?: () => void;
  approving?: boolean;
  runnable?: boolean;
  runAgain?: boolean;
  onRun?: () => void;
  running?: boolean;
}) {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const timelineQuery = useQuery({
    queryKey: qk.issueTimeline(issueId),
    queryFn: () => fetchIssueTimeline(issueId),
    enabled: !!issueId,
  });
  const commentsQuery = useQuery({
    queryKey: qk.issueComments(issueId),
    queryFn: () => fetchIssueComments(issueId),
    enabled: !!issueId,
  });

  if (timelineQuery.isLoading) return <LoadingState label="Loading timeline…" />;
  if (timelineQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(timelineQuery.error, 'Failed to load timeline')}
        onRetry={() => timelineQuery.refetch()}
      />
    );
  }
  const events = timelineQuery.data ?? [];
  const comments = commentsQuery.data ?? [];
  // Owner-only moderation: `issue:delete` (with no resource) resolves true only
  // for a workspace owner — no project role confers it, and there is no
  // platform wildcard. A non-owner may still delete their OWN comment (CommentCard).
  const canModerate = can('issue:delete');
  const currentUserId = user?.id ?? '';

  const feed: FeedItem[] = [
    ...events.map((event) => ({ at: event.at, kind: 'event' as const, event })),
    ...comments.map((comment) => ({ at: comment.createdAt, kind: 'comment' as const, comment })),
  ].sort((a, b) => a.at.localeCompare(b.at));

  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold text-ink">Timeline</h2>
      {feed.length > 0 && (
        <ul className="relative mb-5 space-y-5 before:absolute before:bottom-1 before:left-3 before:top-1 before:w-px before:bg-line">
          {feed.map((item) =>
            item.kind === 'event' ? (
              <TimelineEvent key={item.event.id} event={item.event} />
            ) : (
              <TimelineComment
                key={`comment-${item.comment.id}`}
                comment={item.comment}
                issueId={issueId}
                currentUserId={currentUserId}
                canModerate={canModerate}
                readOnly={readOnly}
              />
            ),
          )}
        </ul>
      )}
      {!readOnly && (
        <CommentComposer
          issueId={issueId}
          heldForYou={heldForYou}
          onApprove={onApprove}
          approving={approving}
          runnable={runnable}
          runAgain={runAgain}
          onRun={onRun}
          running={running}
        />
      )}
    </div>
  );
}
